import React, { useEffect, useState } from "react";
import axios from "axios";
import { ClipboardList, Sun, Moon, Coffee, Stethoscope, Users, CalendarCheck, Inbox } from "lucide-react";
import { API } from "../../shared/config";
import { getPhotoUrl } from "../../shared/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { estDirecteur } from '../../lib/droits';

const today = () => new Date().toISOString().split('T')[0];

const buildSlot = (creneaux, users, me) => {
  const mine = creneaux.find(c => c.employe_id === me?.id);
  if (!mine) return null;
  const collegues = [];
  if (me?.role === 'Médecin') {
    creneaux.forEach(c => { if (c.medecin_ids?.includes(me.id) || c.medecin_attribue_id === me.id) { const u = users.find(x => x.id === c.employe_id) || c.employe; if (u) collegues.push(`${u.prenom} ${u.nom}`); } });
  } else if (me?.role === 'Assistant') {
    const ids = new Set([...(mine.medecin_ids || []), mine.medecin_attribue_id].filter(Boolean));
    ids.forEach(id => { const u = users.find(x => x.id === id); if (u) collegues.push(`Dr ${u.nom}`); });
  }
  return { salle: mine.salle_attribuee || '', salleAttente: mine.salle_attente || '', collegues };
};

const Slot = ({ tone, icon: I, label, slot, withCollegues, testId }) => (
  <div className={`opn-fxp__slot opn-fxp__slot--${tone}`} data-testid={testId}>
    <div className="opn-fxp__slot-head"><I className="h-4 w-4" />{label}</div>
    <div className="opn-fxp__slot-body">
      <p>Salle : <b className="opn-fxp__salle">{slot.salle || 'non attribuée'}</b></p>
      <p>Salle d'attente : <strong>{slot.salleAttente || 'non attribuée'}</strong></p>
      {withCollegues && <p>Avec : <strong>{slot.collegues.length ? slot.collegues.join(', ') : 'personne d\'attribué'}</strong></p>}
    </div>
  </div>
);

const Repos = ({ text, testId }) => (
  <div className="opn-fxp__slot opn-fxp__slot--green" data-testid={testId}>
    <div className="opn-fxp__slot-head"><Coffee className="h-4 w-4" />{text}</div>
  </div>
);

const Blocs = ({ blocs, showMedecin }) => (
  <div className="opn-fxp__slot opn-fxp__slot--blue" data-testid="fxp-blocs">
    <div className="opn-fxp__slot-head"><Stethoscope className="h-4 w-4" />Bloc opératoire</div>
    <div className="opn-fxp__slot-body">
      {blocs.map((b, i) => (
        <p key={i}><span className="opn-fxp__dot" style={{ background: b.type_couleur || '#38bdf8' }} /><strong>{b.type_titre || 'Bloc'}</strong>
          {(b.heure_debut || b.heure_fin) && <span className="opn-fxp__hl"> {b.heure_debut || '?'}–{b.heure_fin || '?'}</span>}
          {showMedecin && (b.medecin_nom) && <span className="opn-fxp__muted"> — {b.medecin_nom}</span>}
        </p>
      ))}
    </div>
  </div>
);

const Directeur = ({ s, blocs }) => {
  if (!s) return <div className="opn-fxp__skeleton" />;
  const plural = (n, w) => `${n} ${w}${n > 1 ? 's' : ''}`;
  return (<>
    <div className="opn-fxp__slot opn-fxp__slot--cyan" data-testid="fxp-effectif">
      <div className="opn-fxp__slot-head"><Users className="h-4 w-4" />Effectif du jour</div>
      <div className="opn-fxp__slot-body">
        <p><strong>{plural(s.nb_medecins, 'médecin')}</strong> et <strong>{plural(s.nb_assistants, 'assistant')}</strong> aujourd'hui.</p>
        {(s.ouverture?.length > 0 || s.fermeture?.length > 0) && <p className="opn-fxp__muted">
          {s.ouverture?.length > 0 && <>Ouverture : <strong>{s.ouverture.join(' et ')}</strong></>}
          {s.ouverture?.length > 0 && s.fermeture?.length > 0 && ' · '}
          {s.fermeture?.length > 0 && <>Fermeture : <strong>{s.fermeture.join(' et ')}</strong></>}
        </p>}
      </div>
    </div>
    {blocs.length > 0 ? <Blocs blocs={blocs} showMedecin /> : <Repos text="Pas de bloc opératoire aujourd'hui." testId="fxp-no-bloc" />}
    <div className="opn-fxp__pills">
      <span className={`opn-fxp__pill ${s.taches_pending > 0 ? 'is-amber' : 'is-green'}`}><CalendarCheck className="h-3.5 w-3.5" />{s.taches_pending > 0 ? `${plural(s.taches_pending, 'tâche')} en attente` : 'Aucune tâche en attente'}</span>
      {s.conges_pending > 0 && <span className="opn-fxp__pill is-orange"><Coffee className="h-3.5 w-3.5" />{plural(s.conges_pending, 'demande')} de congés</span>}
      {s.demandes_pending > 0 && <span className="opn-fxp__pill is-violet"><Inbox className="h-3.5 w-3.5" />{plural(s.demandes_pending, 'demande')} de créneaux</span>}
    </div>
  </>);
};

const PlanningJourFutur = ({ user }) => {
  const [st, setSt] = useState({ loaded: false, matin: null, am: null, blocs: [], synth: null });
  const isDirecteur = estDirecteur(user, 'admin');
  const isMedecin = user?.role === 'Médecin';
  const withCollegues = isMedecin || user?.role === 'Assistant';

  useEffect(() => {
    const d = today();
    Promise.all([
      axios.get(`${API}/planning?date_debut=${d}&date_fin=${d}&_t=${Date.now()}`).catch(() => ({ data: [] })),
      axios.get(`${API}/users`).catch(() => ({ data: [] })),
    ]).then(([pr, ur]) => {
      const users = Array.isArray(ur.data) ? ur.data : [];
      const cr = (Array.isArray(pr.data) ? pr.data : []).filter(c => c.date === d && !c.est_repos);
      setSt(s => ({ ...s, loaded: true, matin: buildSlot(cr.filter(c => c.creneau === 'MATIN'), users, user), am: buildSlot(cr.filter(c => c.creneau === 'APRES_MIDI'), users, user) }));
    });
    axios.get(`${API}/bloc/date-config?date=${d}`).then(r => setSt(s => ({ ...s, blocs: r.data?.configs || [] }))).catch(() => {});
    if (isDirecteur) axios.get(`${API}/directeur/synthese-jour`).then(r => setSt(s => ({ ...s, synth: r.data }))).catch(() => {});
  }, [user, isDirecteur]);

  const mesBlocs = isDirecteur ? st.blocs : st.blocs.filter(b => b.medecin_id === user?.id);
  const nom = isMedecin ? `Docteur ${user?.nom || ''}` : user?.prenom;
  const enRepos = st.loaded && !isDirecteur && !st.matin && !st.am;
  const dateFr = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <section className="opn-fxp" data-testid="fx-planning-jour">
      <header className="opn-fxp__head">
        <span className="opn-fxp__kicker"><ClipboardList className="h-3.5 w-3.5" /> Mon planning du jour</span>
        <span className="opn-fxp__date">{dateFr}</span>
      </header>
      <div className="opn-fxp__who">
        <span className="opn-fxp__ring"><Avatar className="opn-fxp__avatar">
          {user?.photo_url && <AvatarImage src={getPhotoUrl(user.photo_url)} alt="" />}
          <AvatarFallback className="opn-fxp__fallback">{user?.prenom?.[0]}{user?.nom?.[0]}</AvatarFallback>
        </Avatar></span>
        <h2 className="opn-fxp__hello" data-testid="fxp-hello">Bonjour {nom} !</h2>
      </div>
      <div className="opn-fxp__body">
        {!st.loaded ? <div className="opn-fxp__skeleton" data-testid="fxp-loading" /> : isDirecteur ? <Directeur s={st.synth} blocs={mesBlocs} /> : enRepos ? (
          <Repos text="Aujourd'hui tu es en repos ! Profite bien de ta journée." testId="fxp-repos" />
        ) : (<>
          {st.matin && <Slot tone="amber" icon={Sun} label="Ce matin" slot={st.matin} withCollegues={withCollegues} testId="fxp-matin" />}
          {st.am && <Slot tone="violet" icon={Moon} label="Cet après-midi" slot={st.am} withCollegues={withCollegues} testId="fxp-apres-midi" />}
          {st.matin && !st.am && <Repos text="Cet après-midi, tu es en repos." testId="fxp-repos-am" />}
          {!st.matin && st.am && <Repos text="Ce matin, tu es en repos." testId="fxp-repos-matin" />}
          {mesBlocs.length > 0 && <Blocs blocs={mesBlocs} />}
        </>)}
      </div>
    </section>
  );
};

export default PlanningJourFutur;
